import { Clock, MessagesSquare, Hammer, Coins, Heart, BookOpenCheck, PartyPopper, Swords } from "lucide-react";
import { ASSETS } from "@/assets";
import { Reveal } from "@/components/Reveal";
import { Gallery } from "@/components/about/Gallery";
import { DISCORD_URL } from "@/constants/links";
import { ABOUT_PAGE } from "@/constants/testIds";

const DIFFERENCES = [
  {
    icon: Clock,
    title: "Svět běží 24/7",
    desc: "Připoj se kdykoliv, ne jen na domluvenou session. Server běží nonstop a nic ti tak nebrání jít, objevovat a seznamovat se.",
  },
  {
    icon: MessagesSquare,
    title: "RP bez dozoru GM",
    desc: "Naprostá většina hraní probíhá přímo mezi hráči, bez nutnosti GameMastera u každého rozhovoru.",
  },
  {
    icon: Heart,
    title: "Vztahy a přátelství",
    desc: "Navazuj vztahy, buduj rivality i přátelství, které přežijí celou věčnost.",
  },
  {
    icon: Hammer,
    title: "Výrobní profese",
    desc: "Kovej, vař lektvary, vař chutná jídla nebo třeba medovinu - tvá postava může mít skutečné řemeslo.",
  },
  {
    icon: Coins,
    title: "Obchod a ekonomika",
    desc: "Kupuj, prodávej, vlastni podíl v podniku. Hospodářství města utvářejí hráči.",
  },
  {
    icon: BookOpenCheck,
    title: "Život mezi sezeními",
    desc: "Co dělá tvá postava v běžném týdnu, když zrovna neprobíhá kampaň? Tady to zjistíš.",
  },
];

export const AboutPage = () => {
  return (
    <section data-testid={ABOUT_PAGE.section}>
      {/* Hero */}
      <div
        className="relative overflow-hidden px-6 pb-20 pt-40"
        data-testid={ABOUT_PAGE.heroSection}
      >
        <img
          src={ASSETS.heroCastle}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-[65%_center] brightness-[0.85]"
        />
        <div className="absolute inset-0 bg-[#141417]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#141417]/70 to-[#141417]" />
        <div className="relative mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              O Projektu
            </p>
            <h1 className="mt-4 font-display text-4xl font-600 uppercase tracking-tight text-white sm:text-5xl">
              Draconia RP
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/60">
              WoW RolePlay projekt zasazený do světa Zapomenutých říší. Příběh, lore i
              postavy, to vše je v rámci našeho příběhu zasazeno na kontinent Faerûn,
              konkrétně do oblasti Měsíčního moře a mladého města Rowengard.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Co je Draconia RP */}
      <div className="px-6 py-16 lg:py-20" data-testid={ABOUT_PAGE.whatSection}>
        <div className="mx-auto grid max-w-[1584px] grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Co je Draconia RP
            </p>
            <h2 className="mt-3 font-display text-2xl font-600 uppercase tracking-tight text-white sm:text-3xl">
              WoW coby prostředek, Faerûn uvnitř
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-white/60">
              Draconia RP je WoW RolePlay projekt. Neznamená to ale, že hrajeme ve světě
              World of Warcraft - hru WoW 3.3.5a používáme pouze jako prostředek ke hraní.
              Příběh, lore i postavy jsou zasazené do světa Zapomenutých říší, tedy Forgotten
              Realms, a RP souboje, mechaniky i příběhové kampaně se řídí velmi lehce
              modifikovanými pravidly D&D 5E.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              Naším světem je Faerûn, kontinent Zapomenutých říší, se vším, co k němu patří -
              vlastní geografií, frakcemi, božstvy i politikou. Souboje, záchranné hody,
              kouzla i vlastnosti postavy tak dávají smysl i hráčům, kteří D&D znají ze
              stolní verze.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="overflow-hidden rounded-xl border border-white/10">
            <img
              src={ASSETS.diceBook}
              alt="Pravidla D&D 5E používaná na Draconia RP"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </Reveal>
        </div>
      </div>

      {/* Čím se lišíme */}
      <div className="px-6 py-16 lg:py-20" data-testid={ABOUT_PAGE.differenceSection}>
        <div className="mx-auto max-w-[1584px] rounded-xl border border-white/10 bg-[#17171b] px-6 py-14 sm:px-10 lg:py-16">
          <Reveal className="mb-14 text-center">
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Tak trochu jiné DnD sezení
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Nečekej a prostě hraj
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/60">
              Zajímalo tě někdy, co dělá tvá postava, když zrovna neprobíhá kampaňové
              sezení? Na Draconia RP si to nemusíš jen domýšlet - můžeš to prožít.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {DIFFERENCES.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={d.title} delay={i * 0.06} className="group flex flex-col items-center text-center">
                  <span className="mb-5 grid h-16 w-16 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F] transition-all duration-300 group-hover:border-[#F0801F] group-hover:bg-[#F0801F]/10">
                    <Icon className="h-7 w-7" strokeWidth={1.4} />
                  </span>
                  <h3 className="font-display text-sm font-600 uppercase tracking-[0.1em] text-white">
                    {d.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/50">{d.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      {/* Eventy */}
      <div className="px-6 py-16 lg:py-20" data-testid={ABOUT_PAGE.eventsSection}>
        <div className="mx-auto max-w-[1584px]">
          <Reveal className="mb-12 text-center">
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Eventy
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Ať už chceš klábosit, nebo bojovat
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal className="rounded-xl border border-white/10 bg-[#17171b] p-8">
              <span className="grid h-14 w-14 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
                <PartyPopper className="h-6 w-6" strokeWidth={1.4} />
              </span>
              <h3 className="mt-5 font-display text-lg font-600 uppercase tracking-tight text-white">
                Sociální eventy
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                Slavnosti, bály, partičky pokeru v hostinci a další příležitosti, kde se
                postavy potkávají, klevetí a proplétají své osudy - bez kostek, jen s RP.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="rounded-xl border border-white/10 bg-[#17171b] p-8">
              <span className="grid h-14 w-14 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
                <Swords className="h-6 w-6" strokeWidth={1.4} />
              </span>
              <h3 className="mt-5 font-display text-lg font-600 uppercase tracking-tight text-white">
                Kampaňové eventy
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                Plnohodnotná DnD session pod vedením DM - s příběhem, výzvami a obvykle i
                pár uťatými hlavami nepřátel.
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="px-6 py-16 lg:py-20" data-testid={ABOUT_PAGE.gallerySection}>
        <div className="mx-auto max-w-[1584px]">
          <Reveal className="mb-10 text-center">
            <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Galerie
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Chvíle ze světa Draconie
            </h2>
          </Reveal>
          <Gallery />
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 pb-24 pt-4" data-testid={ABOUT_PAGE.ctaSection}>
        <Reveal className="mx-auto max-w-[1584px] rounded-xl border border-[#F0801F]/30 bg-[#17171b] px-6 py-14 text-center sm:px-10">
          <h2 className="font-display text-2xl font-600 uppercase tracking-tight text-white sm:text-3xl">
            Připraven žít v Zapomenutých říších?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-white/60">
            Přidej se na Discord, poznej komunitu a vytvoř si postavu, která si zaslouží
            vlastní příběh.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-[#F0801F] px-8 py-4 font-display text-sm font-600 uppercase tracking-[0.14em] text-[#141417] transition-colors hover:bg-[#ff9438]"
            >
              <i className="fa-brands fa-discord" />
              Discord
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
